import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';
import { useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import warningIcon from '../../../../../Assets/icons/warning.svg';

/**
 * One quiet line: icon, "Allergens:" and the tags. Sits below the choices so
 * it informs without interrupting the path to the add button.
 */
const Row = styled.div<{ onPhoto: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  color: ${(props) => (props.onPhoto ? 'var(--colorWhite)' : 'var(--eposText)')};

  /* Over the hero photo: a frosted glass strip (same recipe as GlassPill) so the row never blends into the picture. */
  ${(props) =>
    props.onPhoto &&
    `
    align-self: flex-start;
    /* Same vertical padding as the small GlassPill so, in the hero corner, it lines up with the back pill. */
    padding: 10px 18px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(10px);
  `}
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.55;
`;

const Tag = styled.span<{ onPhoto: boolean }>`
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  /* Over the photo the venue border color may vanish against the scrim, so use a translucent white. */
  border: 1px solid
    ${(props) => (props.onPhoto ? 'rgba(255, 255, 255, 0.55)' : 'var(--eposBorderColor)')};
  opacity: 0.85;
`;

interface Props {
  /** Raw tags from the item, e.g. "gluten" - capitalised like the kiosk does. Empty shows "Inga"/"None". */
  tags: string[];
  /** Set when rendered inside the hero, over the photo's dark scrim - switches to white type. */
  onPhoto?: boolean;
}

const AllergenList: FC<Props> = ({ tags, onPhoto = false }) => {
  const { i18n } = useI18n();
  const color = onPhoto ? 'var(--colorWhite)' : 'var(--eposText)';

  return (
    <Row data-testid="epos-allergens" onPhoto={onPhoto}>
      <Icon src={warningIcon} color={color} width="15px" height="15px" />
      <Label>{i18n('SSK.Allergens')}</Label>
      {tags.length === 0 && <Tag onPhoto={onPhoto}>{i18n('Express.Allergens.None')}</Tag>}
      {tags.map((tag) => (
        <Tag key={tag} onPhoto={onPhoto}>
          {_.capitalize(tag)}
        </Tag>
      ))}
    </Row>
  );
};

export default memo(AllergenList);
